// The app-wide blocking modals' frame (Figma "App-wide modals & banners · web":
// We've updated our terms, Strava consent, Trial ended): centred over the
// page dimmed with bg/canvas, a 24-radius bg/panel card, 32 padding, 14 apart,
// actions as small buttons on the right. A bottom sheet on a phone.
import React from 'react';
import { Check } from 'lucide-react';
import { Checkbox } from '../ui/checkbox';
import { cn } from '../../lib/utils';

export function ModalShell({ labelledBy, width = 520, z = 'z-[10000]', onScrim, children, className }) {
  return (
    <div className={cn('fixed inset-0 flex items-end justify-center bg-bg-canvas/75 p-0 font-body sm:items-center sm:p-4', z)}
      role="dialog" aria-modal="true" aria-labelledby={labelledBy} onClick={onScrim}>
      <div onClick={onScrim ? (e) => e.stopPropagation() : undefined} style={{ maxWidth: width }}
        className={cn('flex max-h-[100dvh] w-full flex-col gap-3.5 overflow-y-auto rounded-t-hero border border-border-default bg-bg-panel p-6 pb-[calc(1.5rem+var(--safe-bottom))] shadow-[0_24px_64px_rgba(0,0,0,0.5)] sm:max-h-[calc(100dvh-2rem)] sm:rounded-hero sm:p-8', className)}>
        {children}
      </div>
    </div>
  );
}

export function ModalTitle({ id, children }) {
  return <h2 id={id} className="m-0 type-title-page text-text-primary">{children}</h2>;
}

/** The intro line under the title. */
export function ModalLead({ children }) {
  return <p className="m-0 type-body text-text-secondary">{children}</p>;
}

/** Points with a check each (Figma: 16 icon, 10 gap, body small). */
export function CheckList({ items }) {
  return (
    <ul className="m-0 flex list-none flex-col gap-3.5 p-0">
      {items.map((item, i) => (
        <li key={typeof item === 'string' ? item : i} className="flex items-start gap-2.5 type-body-small text-text-body">
          <Check size={16} strokeWidth={2} className="mt-0.5 shrink-0 text-text-secondary" aria-hidden="true" />
          <span className="min-w-0 flex-1">{item}</span>
        </li>
      ))}
    </ul>
  );
}

/** Figma "Checkbox row": a 22 box, 12 gap, the words (body, primary) are the label. */
export function CheckRow({ id, checked, onChange, disabled = false, children }) {
  return (
    <div className="flex items-start gap-3">
      <Checkbox id={id} checked={checked} disabled={disabled} className="h-[22px] w-[22px] rounded-[6px] border-[1.5px]"
        onCheckedChange={(v) => onChange(v === true)} />
      <label htmlFor={id} className="cursor-pointer select-none type-body text-text-primary">{children}</label>
    </div>
  );
}

/** What went wrong, in the modal. */
export function ModalError({ children }) {
  if (!children) return null;
  return <p role="alert" className="m-0 type-body-small text-status-danger">{children}</p>;
}

/** The actions: small buttons, right-aligned (stacked full-width on a phone). */
export function ModalActions({ children, className }) {
  return (
    <div className={cn('mt-1 flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end', className)}>
      {children}
    </div>
  );
}
